import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import type { DetailSection } from '@/shared/app-detail-layout/types';
import type { Tenant } from '@/api/schemas/tenants';
import { FiExternalLink } from 'react-icons/fi';
import { InvitationsPanel } from './invitations-panel';
import { OnboardingReviewPanel } from './onboarding-review-panel';
import { VerificationReviewPanel } from './verification-review-panel';

/** Tenant sections. Each is a typed descriptor — the layout is unaware of tenants. */
export function buildSections(tenant: Tenant): DetailSection[] {
  return [
    {
      id: 'overview',
      label: 'Overview',
      content: <OverviewSection tenant={tenant} />,
    },
    {
      id: 'administrators',
      label: 'Administrators',
      content: <InvitationsPanel tenantId={tenant.id} />,
    },
    {
      id: 'onboarding',
      label: 'Onboarding',
      content: <OnboardingReviewPanel tenantId={tenant.id} />,
    },
    {
      id: 'verification',
      label: 'Verification',
      content: <VerificationReviewPanel tenantId={tenant.id} />,
    },
    {
      id: 'media',
      label: 'Media',
      content: <p className="text-sm text-content-muted">No media uploaded yet.</p>,
    },
    {
      id: 'capabilities',
      label: 'Capabilities',
      content: (
        <p className="text-sm text-content-muted">
          Capability configuration will be editable from this surface once the entitlement API ships.
        </p>
      ),
    },
  ];
}

function OverviewSection({ tenant }: { tenant: Tenant }): ReactNode {
  return (
    <div className="space-y-6">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Slug" value={tenant.slug} />
        <Field label="Status">
          <Badge tone={tenant.status === 'active' ? 'positive' : 'caution'}>{tenant.status}</Badge>
        </Field>
        <Field label="Legal Entity" value={tenant.legalEntityId ?? '—'} />
        <Field label="Version" value={String(tenant.version)} />
        <Field label="Created">{new Date(tenant.createdAt).toLocaleString()}</Field>
        <Field label="Updated">{new Date(tenant.updatedAt).toLocaleString()}</Field>
      </dl>

      <div className="rounded-lg border border-border p-4 bg-surface-sunken">
        <h4 className="font-medium mb-3">Tenant Workspace</h4>
        <p className="text-sm text-content-muted mb-3">
          Configure business information, verticals, locations, service areas, and branding.
        </p>
        <a
          href={`/tenant/${tenant.id}/workspace`}
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          Open Workspace
          <FiExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

function Field({ label, value, children }: { label: string; value?: string; children?: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{children ?? value}</dd>
    </div>
  );
}
